/**
 * Admin Portal → Partners (home) — `/admin/partners`.
 *
 * One row per partner (status, tags, latest statement), a top strip (owed this month,
 * overdue, drafts awaiting review), the Health tab (GET /partners/health) and the
 * 3-step "New partner" flow (details → first tag with its offer and bounties → links).
 * Codes PR-2 (2026-09-28): a monthly discount picks its length (1, 3, 6 or 12 months), and a
 * summary WARNING lists every live link whose discount some store product can't sell yet
 * (GET /partners/discount-coverage).
 * Spec: docs/handoff/PARTNERS_ADMIN_FE_HANDOFF.md §2.1, §2.2, §2.7.
 *
 * Web-only (the admin SPA): react-router is fine here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminButton, AdminCard, SectionHeader } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import {
  DEFAULT_MONTHLY_TERM,
  coverageSummary,
  defaultMonthlyTerm,
  monthlyTermChoices,
  monthlyTermError,
  tagCoverageWarnings,
  termChoiceLabel,
  type DiscountCoverage,
  type DiscountOffer,
} from '../../../components/admin/discountTermsLogic';
import {
  DEFAULT_BOUNTY_ANNUAL_INPUT,
  DEFAULT_BOUNTY_MONTHLY_INPUT,
  DEFAULT_PARTNER_TRIAL_DAYS,
  LINK_DESTINATIONS,
  TAG_RE,
  TRIAL_CHOICES,
  createPartnerBody,
  discountChoices,
  emptyPartnerForm,
  formatCents,
  healthNumbersText,
  homeStrip,
  isOverdue,
  isoDay,
  latestStatementByPartner,
  parseDollarsToCents,
  priorMonth,
  statusBadge,
  trialLabel,
  type DiscountLevel,
  type HealthAlert,
  type LinkRow,
  type PartnerForm,
  type PartnerRow,
  type StatementRow,
} from './partnersLogic';
import {
  NoticeLine,
  ProgramBanner,
  ReadOnlyNote,
  StatusBadge,
  copyText,
  errText,
  fmtDate,
  hint,
  input,
  label,
  row,
  td,
  th,
  useAdminRole,
  useBusy,
  useProgramStatus,
  type Notice,
} from './PartnersShared';

type Tab = 'partners' | 'health';

export function AdminPartners() {
  const navigate = useNavigate();
  const { role, writable, loaded: roleLoaded } = useAdminRole();
  const program = useProgramStatus();
  const [tab, setTab] = useState<Tab>('partners');
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [statements, setStatements] = useState<StatementRow[]>([]);
  const [alerts, setAlerts] = useState<HealthAlert[] | null>(null);
  const [healthAsOf, setHealthAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice>(null);
  const [showNew, setShowNew] = useState(false);
  const [coverage, setCoverage] = useState<DiscountCoverage | undefined>(undefined);
  const [busy, run] = useBusy();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([PartnersAdmin.listPartners(), PartnersAdmin.listStatements()]);
      setPartners(p.partners || []);
      setStatements(s.statements || []);
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    } finally {
      setLoading(false);
    }
    // Best effort: the summary of discounts no store offer backs (never blocks the page).
    PartnersAdmin.discountCoverage().then(setCoverage).catch(() => setCoverage(undefined));
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const h = await PartnersAdmin.health();
      setAlerts(h.alerts || []);
      setHealthAsOf(h.as_of);
    } catch (e) {
      setAlerts(null);
      setNotice({ kind: 'error', text: `Health: ${errText(e)}` });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (tab === 'health' && alerts === null) loadHealth(); }, [tab, alerts, loadHealth]);

  const today = isoDay(new Date());
  const strip = useMemo(() => homeStrip(statements, today), [statements, today]);
  const latest = useMemo(() => latestStatementByPartner(statements), [statements]);
  const drafts = statements.filter((s) => s.status === 'draft');

  const draftPriorMonth = () =>
    run(async () => {
      const { year, month } = priorMonth(new Date());
      try {
        const res = await PartnersAdmin.draft({ year, month });
        const n = res.drafted?.length ?? 0;
        setNotice({
          kind: 'ok',
          text: n
            ? `Drafted ${n} statement${n === 1 ? '' : 's'} for ${year}-${String(month).padStart(2, '0')}: ${res.drafted.map((d) => d.number).join(', ')}.`
            : `Nothing to draft for ${year}-${String(month).padStart(2, '0')} (no unbilled entries, or already drafted).`,
        });
        await refresh();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  return (
    <div style={{ padding: 24, maxWidth: 1280 }}>
      <SectionHeader title="Partners" subtitle="Referral partners, their links and codes, and the monthly statements we pay them." />
      <ProgramBanner status={program.status} error={program.error} />
      <ReadOnlyNote writable={writable} loaded={roleLoaded} role={role} />
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      {coverageSummary(coverage) && (
        <AdminCard style={{ marginBottom: 16, background: 'var(--status-warning-bg, #fffbeb)' }}>
          <div data-testid="discount-coverage-summary" style={{ fontSize: 13, color: 'var(--status-warning-text, #92400e)' }}>
            <b>⚠ {coverageSummary(coverage)}</b>
            {(coverage?.tags ?? []).filter((t) => !t.buyable_everywhere).map((t) => (
              <div key={t.tag_id} style={{ marginTop: 4 }}>
                <Link to={`/admin/partners/${t.partner_id}?tag=${t.tag_id}`}>{t.partner_name} / {t.tag}</Link>: {tagCoverageWarnings(t).join(' ')}
              </div>
            ))}
          </div>
        </AdminCard>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        <AdminCard>
          <div style={label} title="Approved or sent statements not yet paid (sum of their totals).">Owed</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCents(strip.owedCents)}</div>
          <div style={hint}>{strip.owedCount} statement{strip.owedCount === 1 ? '' : 's'} approved or sent, unpaid</div>
        </AdminCard>
        <AdminCard>
          <div style={label} title="Owed statements whose due date is before today.">Overdue</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: strip.overdueCount ? 'var(--status-error-text, #991b1b)' : undefined }}>
            {formatCents(strip.overdueCents)}
          </div>
          <div style={hint}>{strip.overdueCount} past due</div>
        </AdminCard>
        <AdminCard>
          <div style={label}>Drafts awaiting review</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{strip.draftCount}</div>
          {drafts[0] && (
            <Link to={`/admin/partners/statements/${drafts[0].id}`} style={{ fontSize: 12 }}>
              Open the next draft ({drafts[0].number}) →
            </Link>
          )}
        </AdminCard>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <AdminButton size="sm" variant={tab === 'partners' ? 'primary' : 'secondary'} onClick={() => setTab('partners')}>Partners</AdminButton>
        <AdminButton size="sm" variant={tab === 'health' ? 'primary' : 'secondary'} onClick={() => setTab('health')}>Health</AdminButton>
        <div style={{ flex: 1 }} />
        {writable && (
          <>
            <AdminButton size="sm" variant="secondary" onClick={draftPriorMonth} disabled={busy}
              title="The cron drafts the prior month on the 1st–7th; this runs the same draft now for every partner.">
              Draft last month's statements
            </AdminButton>
            <AdminButton size="sm" onClick={() => setShowNew((v) => !v)}>{showNew ? 'Close' : 'New partner'}</AdminButton>
          </>
        )}
      </div>

      {showNew && writable && (
        <NewPartnerFlow
          onDone={(id) => { setShowNew(false); refresh(); navigate(`/admin/partners/${id}`); }}
          onCreated={refresh}
        />
      )}

      {tab === 'partners' && (
        <AdminCard flush>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Partner</th>
                <th style={th}>Code</th>
                <th style={th}>Status</th>
                <th style={th}>Tags</th>
                <th style={th}>Latest statement</th>
                <th style={th}>Due</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td style={td} colSpan={6}>Loading…</td></tr>}
              {!loading && partners.length === 0 && (
                <tr><td style={td} colSpan={6}>No partners yet.{writable ? ' Use "New partner" to add the first.' : ''}</td></tr>
              )}
              {partners.map((p) => {
                const s = latest[p.id];
                return (
                  <tr key={p.id}>
                    <td style={td}><Link to={`/admin/partners/${p.id}`}><b>{p.display_name}</b></Link><div style={hint}>{p.legal_name}</div></td>
                    <td style={td}><code>{p.code}</code></td>
                    <td style={td}><StatusBadge status={p.status} tone={statusBadge(p.status)} /></td>
                    <td style={td}>{p.tag_count}</td>
                    <td style={td}>
                      {s ? (
                        <Link to={`/admin/partners/statements/${s.id}`}>
                          {s.number} · <StatusBadge status={s.status} tone={statusBadge(s.status)} /> · {formatCents(s.total_cents, s.currency)}
                        </Link>
                      ) : '—'}
                    </td>
                    <td style={{ ...td, color: s && isOverdue(s, today) ? 'var(--status-error-text, #991b1b)' : undefined }}>
                      {s ? fmtDate(s.due_date) : '—'}{s && isOverdue(s, today) ? ' (overdue)' : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </AdminCard>
      )}

      {tab === 'health' && (
        <AdminCard>
          <div style={{ ...row, justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={hint}>As of {healthAsOf ? new Date(healthAsOf).toLocaleString() : '—'}</div>
            <AdminButton size="sm" variant="secondary" onClick={loadHealth}>Refresh</AdminButton>
          </div>
          {alerts === null && <div style={hint}>Loading…</div>}
          {alerts && alerts.length === 0 && <div>All quiet — no tag needs a look.</div>}
          {alerts && alerts.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Alert</th><th style={th}>Partner / tag</th><th style={th}>Message</th><th style={th}>Numbers</th></tr></thead>
              <tbody>
                {alerts.map((a, i) => (
                  <tr key={`${a.code}-${a.tag_id}-${i}`}>
                    <td style={td}><code>{a.code}</code></td>
                    <td style={td}>
                      <Link to={`/admin/partners/${a.partner_id}${a.tag_id ? `?tag=${a.tag_id}` : ''}`}>{a.partner_name}{a.tag ? ` / ${a.tag}` : ''}</Link>
                    </td>
                    <td style={td}>{a.message}</td>
                    <td style={{ ...td, fontSize: 12 }}>{healthNumbersText(a.numbers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AdminCard>
      )}
    </div>
  );
}

// ── New partner: details → first tag → links ─────────────────────────────────

const NewPartnerFlow: React.FC<{ onDone: (partnerId: string) => void; onCreated: () => void }> = ({ onDone, onCreated }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<PartnerForm>(emptyPartnerForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [tagId, setTagId] = useState<string | null>(null);
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [levels, setLevels] = useState<DiscountLevel[]>([]);
  const [offers, setOffers] = useState<DiscountOffer[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  // The first tag starts at the launch defaults (owner decision 2026-10-17): a free month, CAD 10 / CAD 30.
  const [tag, setTag] = useState({ tag: '', name: '', bountyMonthly: DEFAULT_BOUNTY_MONTHLY_INPUT, bountyAnnual: DEFAULT_BOUNTY_ANNUAL_INPUT,
    trial: DEFAULT_PARTNER_TRIAL_DAYS, dm: '' as string, dmm: String(DEFAULT_MONTHLY_TERM), da: '' as string, clipboard: false });
  const [dests, setDests] = useState<string[]>(['home']);

  useEffect(() => {
    PartnersAdmin.discountLevels().then((r) => setLevels(r.levels || [])).catch(() => setLevels([]));
    PartnersAdmin.discountOffers().then((r) => setOffers(r.offers || [])).catch(() => setOffers([]));
  }, []);

  const set = (k: keyof PartnerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const createPartner = () =>
    run(async () => {
      const { body, errors: errs } = createPartnerBody(form);
      setErrors(errs);
      if (Object.keys(errs).length) return;
      try {
        const res = await PartnersAdmin.createPartner(body);
        setPartnerId(res.partner.id);
        setNotice({ kind: 'ok', text: `Created ${res.partner.code}. Now its first tag.` });
        setStep(2);
        onCreated();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const createTag = () =>
    run(async () => {
      if (!partnerId) return;
      const errs: string[] = [];
      if (!TAG_RE.test(tag.tag)) errs.push('Tag: 2–40 lowercase letters, digits or "-" — it is the /go/<tag> link.');
      if (!tag.name.trim()) errs.push('Name: required.');
      const m = parseDollarsToCents(tag.bountyMonthly);
      const a = tag.bountyAnnual.trim() ? parseDollarsToCents(tag.bountyAnnual) : m;
      if (!m.ok) errs.push(`Monthly bounty: ${m.error}`);
      if (!a.ok) errs.push(`Annual bounty: ${a.error}`);
      const termErr = tag.dm ? monthlyTermError(offers, Number(tag.dm), Number(tag.dmm)) : null;
      if (termErr) errs.push(termErr);
      if (errs.length || !m.ok || !a.ok) { setNotice({ kind: 'error', text: 'Fix these first:', extra: errs }); return; }
      const body: Record<string, unknown> = {
        tag: tag.tag, name: tag.name.trim(), currency: 'CAD', clipboard_handoff: tag.clipboard,
        trial_days: tag.trial, bounty_monthly_cents: m.cents, bounty_annual_cents: a.cents,
      };
      if (tag.dm) {
        body.discount_monthly_pct = Number(tag.dm);
        body.discount_monthly_months = Number(tag.dmm);
      }
      if (tag.da) body.discount_annual_pct = Number(tag.da);
      try {
        const res = await PartnersAdmin.createTag(partnerId, body);
        setTagId(res.tag.id);
        setNotice({ kind: 'ok', text: `Tag ${res.tag.tag} created (${res.tag.offer_summary || 'tracking only'}). Now its links.` });
        setStep(3);
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const buildLinks = () =>
    run(async () => {
      if (!tagId) return;
      try {
        const res = await PartnersAdmin.buildLinks(tagId, dests);
        setLinks(res.links || []);
        setNotice({ kind: 'ok', text: 'Links ready — copy them to the partner.' });
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const levelChoices = discountChoices(levels, null);
  const termChoices = monthlyTermChoices(offers, tag.dm ? Number(tag.dm) : null);
  const field = (k: keyof PartnerForm, text: string, opts: { type?: string; width?: number; help?: string } = {}) => (
    <div style={{ minWidth: opts.width ?? 220 }}>
      <label style={label} htmlFor={`np-${k}`}>{text}</label>
      <input id={`np-${k}`} style={{ ...input, width: '100%' }} type={opts.type ?? 'text'} value={String(form[k])} onChange={set(k)} />
      {errors[k] && <div style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{errors[k]}</div>}
      {opts.help && <div style={hint}>{opts.help}</div>}
    </div>
  );

  return (
    <AdminCard style={{ marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>New partner — step {step} of 3</h3>
      <div style={hint}>Details → first tag and its offer → links.</div>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />

      {step === 1 && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={row}>
            {field('code', 'Partner code', { width: 160, help: 'Uppercase; prints in statement numbers; never changes.' })}
            {field('display_name', 'Display name')}
            {field('legal_name', 'Legal name (on statements)')}
          </div>
          <div style={row}>
            {field('contact_name', 'Contact name')}
            {field('contact_email', 'Contact email', { type: 'email' })}
            {field('statement_email', 'Statement email', { type: 'email', help: 'Statements go here (fallback: contact email).' })}
          </div>
          <div style={row}>
            {field('net_days', 'Net days', { width: 100 })}
            <div>
              <label style={label}><input type="checkbox" checked={form.tax_applies} onChange={set('tax_applies')} /> Tax applies</label>
            </div>
            {field('tax_rate_pct', 'Tax rate %', { width: 100 })}
            {field('tax_number', "Partner's tax number", { width: 180 })}
          </div>
          {field('payment_method_text', 'How we pay them (prints on the statement)', { width: 500 })}
          <div>
            <AdminButton size="sm" onClick={createPartner} disabled={busy}>{busy ? 'Creating…' : 'Create partner'}</AdminButton>
          </div>
        </div>
      )}

      {step === 2 && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={row}>
            <div>
              <label style={label} htmlFor="nt-tag">Tag (the link)</label>
              <input id="nt-tag" style={input} value={tag.tag} onChange={(e) => setTag({ ...tag, tag: e.target.value.toLowerCase() })} placeholder="sportstats-ottawa" />
              <div style={hint}>{tag.tag ? `coachcedar.com/go/${tag.tag}` : 'lowercase, digits, "-"'} — cannot be renamed later.</div>
            </div>
            <div>
              <label style={label} htmlFor="nt-name">Name</label>
              <input id="nt-name" style={input} value={tag.name} onChange={(e) => setTag({ ...tag, name: e.target.value })} placeholder="Ottawa race kit insert" />
            </div>
          </div>
          <div style={row}>
            <div>
              <label style={label} htmlFor="nt-bm">Bounty, monthly plan (CAD)</label>
              <input id="nt-bm" style={{ ...input, width: 120 }} value={tag.bountyMonthly} onChange={(e) => setTag({ ...tag, bountyMonthly: e.target.value })} />
            </div>
            <div>
              <label style={label} htmlFor="nt-ba">Bounty, annual plan (CAD)</label>
              <input id="nt-ba" style={{ ...input, width: 120 }} value={tag.bountyAnnual} onChange={(e) => setTag({ ...tag, bountyAnnual: e.target.value })} placeholder="same as monthly" />
            </div>
            <div>
              <label style={label} htmlFor="nt-trial">Free trial</label>
              <select id="nt-trial" style={input} value={tag.trial} onChange={(e) => setTag({ ...tag, trial: Number(e.target.value) })}>
                {TRIAL_CHOICES.map((d) => <option key={d} value={d}>{trialLabel(d)}</option>)}
              </select>
            </div>
            <div>
              <label style={label} htmlFor="nt-dm">Discount, monthly</label>
              <select id="nt-dm" style={input} value={tag.dm}
                onChange={(e) => setTag({ ...tag, dm: e.target.value,
                  // A new level re-picks its length: never a stale one from the level before (#4217 N3).
                  dmm: String(defaultMonthlyTerm(offers, e.target.value ? Number(e.target.value) : null)) })}>
                <option value="">None</option>
                {levelChoices.map((p) => <option key={p} value={p}>{p}% off</option>)}
              </select>
            </div>
            {tag.dm && (
              <div>
                <label style={label} htmlFor="nt-dmm">Monthly discount lasts</label>
                <select id="nt-dmm" style={input} value={tag.dmm} onChange={(e) => setTag({ ...tag, dmm: e.target.value })}>
                  {termChoices.map((m) => <option key={m} value={m}>the first {termChoiceLabel(m)}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={label} htmlFor="nt-da">Discount, annual (first year)</label>
              <select id="nt-da" style={input} value={tag.da} onChange={(e) => setTag({ ...tag, da: e.target.value })}>
                <option value="">None</option>
                {levelChoices.map((p) => <option key={p} value={p}>{p}% off</option>)}
              </select>
            </div>
          </div>
          <div style={hint}>
            A tag can be tracking only, a longer trial, a discount, or a mix. A monthly discount lasts the first 1, 3, 6 or 12 months (you pick); an annual discount lasts the first year. Each discount and length needs a live offer in both stores (Store access) before anyone can buy with it — the tag shows a warning until it does.
          </div>
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={tag.clipboard} onChange={(e) => setTag({ ...tag, clipboard: e.target.checked })} /> Clipboard handoff (off by default — see the tag editor for what it does)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <AdminButton size="sm" onClick={createTag} disabled={busy}>{busy ? 'Creating…' : 'Create tag'}</AdminButton>
            <AdminButton size="sm" variant="ghost" onClick={() => partnerId && onDone(partnerId)}>Skip — finish later</AdminButton>
          </div>
        </div>
      )}

      {step === 3 && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={row}>
            {LINK_DESTINATIONS.map((d) => (
              <label key={d} style={{ fontSize: 13 }}>
                <input type="checkbox" checked={dests.includes(d)}
                  onChange={(e) => setDests((cur) => (e.target.checked ? [...cur, d] : cur.filter((x) => x !== d)))} /> {d === 'with' ? 'co-branded page' : d}
              </label>
            ))}
            <AdminButton size="sm" onClick={buildLinks} disabled={busy || !dests.length}>Build links</AdminButton>
          </div>
          {links.map((l) => (
            <div key={l.id} style={row}>
              <code style={{ fontSize: 13 }}>{l.url}</code>
              <AdminButton size="sm" variant="ghost" onClick={() => copyText(l.url)}>Copy</AdminButton>
            </div>
          ))}
          <div><AdminButton size="sm" onClick={() => partnerId && onDone(partnerId)}>Done — open the partner</AdminButton></div>
        </div>
      )}
    </AdminCard>
  );
};

export default AdminPartners;
